import { useEffect, useState } from "react";
import { X, Check, Crown, Star, Gem, Printer } from "lucide-react";
import { toast } from "sonner";
import { api, unwrap } from "@/lib/api";
import { GlossLogomark } from "@/components/GlossLogomark";
import { MemberBadge } from "@/components/MemberBadge";

const ICONS = { silver: Star, gold: Crown, platinum: Gem };

/**
 * MembershipEnrolModal
 * Step 1 → Pick plan + duration
 * Step 2 → Pick payment method
 * Step 3 → POST /membership-plans/enrol → MembershipCardReveal
 */
export function MembershipEnrolModal({ client, onClose, onEnrolled }) {
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [duration, setDuration] = useState("monthly");
  const [paymentMethod, setPaymentMethod] = useState("upi");
  const [submitting, setSubmitting] = useState(false);
  const [reveal, setReveal] = useState(null);   // {membership, welcome_points, plan}

  useEffect(() => {
    api.get("/membership-plans").then((r) => {
      const list = unwrap(r) || [];
      setPlans(list);
      const gold = list.find((p) => p.color_scheme === "gold");
      setSelectedPlan(gold || list[0]);
    });
  }, []);

  const fee = selectedPlan ? (duration === "annual" ? selectedPlan.annual_fee : selectedPlan.monthly_fee) : 0;
  const savings = selectedPlan ? selectedPlan.monthly_fee * 12 - selectedPlan.annual_fee : 0;

  const submit = async () => {
    if (!selectedPlan) return;
    setSubmitting(true);
    try {
      const r = await api.post("/membership-plans/enrol", {
        client_id: client.id, plan_id: selectedPlan.id,
        duration, payment_method: paymentMethod,
      });
      setReveal(unwrap(r));
      onEnrolled?.(unwrap(r));
    } catch (e) {
      toast.error("Enrolment failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (reveal) {
    return <MembershipCardReveal client={client} reveal={reveal} onClose={onClose} />;
  }

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4"
         style={{ background: "rgba(28,25,23,0.55)", backdropFilter: "blur(12px)" }}
         data-testid="membership-enrol-modal">
      <div className="glass-elevated rounded-[24px] p-7 w-full max-w-[760px] max-h-[92vh] overflow-y-auto relative anim-slide-up">
        <button onClick={onClose} className="absolute top-4 right-4 w-9 h-9 rounded-full glass-subtle flex items-center justify-center"
                aria-label="Close" data-testid="membership-modal-close">
          <X size={16} />
        </button>

        <div className="overline mb-1">Membership enrolment</div>
        <h2 className="font-sora font-bold text-[22px] tracking-tight mb-5">
          Choose a plan for {client.name}
        </h2>

        {/* Plans grid (compact) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
          {plans.map((p) => {
            const selected = selectedPlan?.id === p.id;
            const Icon = ICONS[p.color_scheme] || Crown;
            return (
              <button
                key={p.id}
                onClick={() => setSelectedPlan(p)}
                className={`plan-card plan-${p.color_scheme} text-left ${selected ? "ring-2 ring-[#8E6A1A]" : ""}`}
                style={{ minHeight: 240, padding: "20px 18px" }}
                data-testid={`enrol-plan-${p.color_scheme}`}
              >
                {p.color_scheme === "gold" && <span className="plan-popular">Most popular</span>}
                <div className="flex items-center justify-between">
                  <Icon size={22} />
                  {selected && <Check size={18} className="text-[#1c1917]" />}
                </div>
                <div className="font-sora font-bold text-[18px] mt-1.5">{p.name}</div>
                <div className="font-mono text-[12.5px] opacity-80">
                  ₹{p.monthly_fee}/mo · ₹{p.annual_fee}/yr
                </div>
                <ul className="space-y-1.5 mt-3 text-[12px]">
                  {p.benefits.slice(0, 3).map((b, i) => (
                    <li key={i} className="plan-checklist-li">
                      <Check size={12} /> <span className="opacity-90">{b}</span>
                    </li>
                  ))}
                </ul>
              </button>
            );
          })}
        </div>

        {/* Duration toggle */}
        <div className="mb-5 flex items-center gap-2">
          <DurationPill v="monthly" cur={duration} onPick={setDuration} label="Monthly" />
          <DurationPill v="annual" cur={duration} onPick={setDuration} label="Annual" suffix={savings > 0 ? `Save ₹${savings}` : ""} />
        </div>

        {/* Payment */}
        <div className="overline mb-2">Payment method</div>
        <div className="flex gap-2 mb-5">
          {["cash", "upi", "card"].map((m) => (
            <button
              key={m}
              onClick={() => setPaymentMethod(m)}
              className={`flex-1 h-12 rounded-full font-sora font-semibold text-[13px] uppercase tracking-wider transition ${paymentMethod === m
                ? "bg-[var(--color-mauve)] text-white shadow-md"
                : "glass-subtle text-[var(--color-text-secondary)]"
              }`}
              data-testid={`enrol-pay-${m}`}
            >{m}</button>
          ))}
        </div>

        {/* Total */}
        <div className="flex items-center justify-between mb-5">
          <span className="font-inter text-[13px] text-[var(--color-text-muted)]">Amount due</span>
          <span className="font-mono text-[32px] text-[var(--color-gold-deeper)]">₹{fee.toLocaleString("en-IN")}</span>
        </div>

        <button
          onClick={submit}
          disabled={!selectedPlan || submitting}
          className="btn-3d btn-gold-3d btn-xl w-full"
          data-testid="enrol-submit-btn"
        >
          {submitting ? "Processing…" : `Process Payment · ₹${fee.toLocaleString("en-IN")}`}
        </button>
      </div>
    </div>
  );
}

function DurationPill({ v, cur, onPick, label, suffix }) {
  const sel = v === cur;
  return (
    <button
      onClick={() => onPick(v)}
      className={`flex items-center gap-2 h-10 px-4 rounded-full font-sora font-semibold text-[12.5px] ${sel
        ? "bg-[var(--color-gold-mid)] text-white shadow-md"
        : "glass-subtle text-[var(--color-text-secondary)]"
      }`}
      data-testid={`enrol-duration-${v}`}
    >
      {label}
      {suffix && (
        <span className={`px-2 py-0.5 rounded-full font-mono text-[10.5px] ${sel ? "bg-white/20" : "bg-[rgba(34,197,94,0.14)] text-[#15803D]"}`}>
          {suffix}
        </span>
      )}
    </button>
  );
}

/**
 * MembershipCardReveal
 * The "wow" moment — 3D card flip + confetti.
 */
export function MembershipCardReveal({ client, reveal, onClose }) {
  const { membership, welcome_points, plan } = reveal;
  const cs = membership.color_scheme || "gold";
  const Icon = ICONS[cs] || Crown;
  const [showConfetti, setShowConfetti] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setShowConfetti(false), 1600);
    return () => clearTimeout(t);
  }, []);

  const confettiPieces = Array.from({ length: 24 }).map((_, i) => {
    const angle = (i / 24) * 360 + Math.random() * 15;
    const colors = ["#D4A853", "#7C5C7A", "#F472B6", "#F0D898", "#B08AAE", "#15803D"];
    const color = colors[i % colors.length];
    return (
      <span key={i} className="piece"
            style={{ "--angle": `${angle}deg`, background: color, animationDelay: `${i * 14}ms` }} />
    );
  });

  return (
    <div className="mc-overlay" data-testid="membership-card-reveal" onClick={onClose}>
      {showConfetti && <div className="mc-confetti">{confettiPieces}</div>}
      <div className="mc-stage" onClick={(e) => e.stopPropagation()}>
        <div className={`mc-card ${cs}`} data-testid="membership-card">
          <div className="sheen" />
          <div className="absolute inset-0 p-6 flex flex-col justify-between">
            <div className="flex items-center justify-between">
              <GlossLogomark size={32} animate={false} />
              <div className="flex items-center gap-1 font-sora font-bold text-[12px] tracking-[0.22em]">
                <Icon size={14} /> {(membership.badge_label || cs).toUpperCase()} MEMBER
              </div>
            </div>
            <div>
              <div className="font-sora font-bold text-[22px] leading-tight tracking-tight">{client.name}</div>
              <div className="font-mono text-[10.5px] mt-1 opacity-70">
                Member since {new Date(membership.valid_from).toLocaleDateString("en-IN", { month: "short", year: "numeric" })}
              </div>
            </div>
            <div className="flex items-end justify-between font-mono text-[10.5px] opacity-75">
              <span>GLOSS Salon · KP</span>
              <span>Valid until {membership.valid_until}</span>
            </div>
          </div>
        </div>

        <div className="mt-8 text-center text-white max-w-[420px] mx-auto">
          <div className="font-sora font-bold text-[22px] mb-1.5 flex items-center justify-center gap-2">
            <Icon size={22} className="text-[#D4A853]" /> Welcome to {plan.name}!
          </div>
          <div className="font-inter text-[13px] opacity-75">
            <span className="text-[#D4A853] font-semibold">+{welcome_points} welcome points</span> credited
          </div>
          <div className="mt-4 flex items-center justify-center gap-2 flex-wrap">
            {plan.benefits.slice(0, 3).map((b) => (
              <span key={b} className="px-3 py-1 rounded-full text-[11.5px] font-sora font-semibold"
                    style={{ background: "rgba(212,168,83,0.20)", color: "#F0D898", border: "1px solid rgba(212,168,83,0.40)" }}>
                {b}
              </span>
            ))}
          </div>
          <div className="mt-6 flex items-center justify-center gap-3">
            <button onClick={() => window.print()} className="btn-3d btn-glass-3d btn-sm">
              <Printer size={14} /> Print Card
            </button>
            <button onClick={onClose} className="btn-3d btn-gold-3d btn-sm" data-testid="card-reveal-done">
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
